import type {
  ApiResponse,
  AppointmentConfirmationResult,
  ChatMessageResponse,
  ConversationResumeResult,
  ManagedAppointment,
  PublicAvailability,
  PublicStudio,
  PublicService,
  TemporaryReservationResult,
} from '@terapeut-ia/shared-types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...init?.headers,
    },
  })

  const raw = await response.text()
  let payload: ApiResponse<T>
  try {
    payload = JSON.parse(raw) as ApiResponse<T>
  } catch {
    throw new Error(`A API retornou uma resposta inválida (${response.status}).`)
  }
  if (!payload.success) throw new Error(payload.error.message)
  return payload.data
}

export function getPublicServices(): Promise<PublicService[]> {
  return apiRequest<PublicService[]>('/v1/public/services')
}

export function getAvailability(serviceId: string, date: string): Promise<PublicAvailability> {
  const query = new URLSearchParams({ serviceId, date })
  return apiRequest<PublicAvailability>(`/v1/public/availability?${query.toString()}`)
}

export function createTemporaryReservation(input: {
  serviceId: string
  startAt: number
  conversationId?: string
  idempotencyKey: string
}): Promise<TemporaryReservationResult> {
  return apiRequest<TemporaryReservationResult>('/v1/public/reservations', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function releaseTemporaryReservation(id: string, conversationId: string): Promise<void> {
  return apiRequest<void>(`/v1/public/reservations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({ conversationId }),
  })
}

export function confirmAppointment(input: {
  reservationId: string
  conversationId: string
  clientName: string
  phone: string
  idempotencyKey: string
}): Promise<AppointmentConfirmationResult> {
  return apiRequest<AppointmentConfirmationResult>('/v1/public/appointments/confirm', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function lookupAppointment(input: {
  phone: string
  managementCode: string
}): Promise<ManagedAppointment> {
  return apiRequest<ManagedAppointment>('/v1/public/appointment-management/lookup', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function cancelAppointment(input: {
  appointmentId: string
  phone: string
  managementCode: string
  reason?: string
  idempotencyKey: string
}): Promise<ManagedAppointment> {
  return apiRequest<ManagedAppointment>('/v1/public/appointment-management/cancel', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function rescheduleAppointment(input: {
  appointmentId: string
  phone: string
  managementCode: string
  startAt: number
  idempotencyKey: string
}): Promise<ManagedAppointment> {
  return apiRequest<ManagedAppointment>('/v1/public/appointment-management/reschedule', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function sendChatMessage(input: {
  conversationId?: string
  message: string
}): Promise<ChatMessageResponse> {
  return apiRequest<ChatMessageResponse>('/v1/public/conversations/messages', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function resumeConversation(input: {
  conversationId?: string
  phone: string
  managementCode: string
}): Promise<ConversationResumeResult> {
  return apiRequest<ConversationResumeResult>('/v1/public/conversations/resume', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export function getPublicStudio(): Promise<PublicStudio> {
  return apiRequest<PublicStudio>('/v1/public/studio')
}
